import type { CmmdContextPaths as CmmdForgePaths } from "@cmmd-forge/contracts";
import { readCloudProjectCatalog } from "./cloudProjectCatalog";
import type { CloudProjectCatalog } from "./cloudProjectCatalog";
import {
  previewCloudProjectRemoval,
  requestCloudProjectRemoval,
  type CmmdCloudProjectRemovalOperation,
  type CmmdCloudProjectRemovalPreview,
} from "./cmmdCloudProjectRemovalApi";
import {
  ensureBearer,
  requestRuntime,
  type CmmdForgeRuntimeApiConfig,
  type CmmdForgeRuntimeLifecycleAction,
} from "./cmmdRuntimeRequest";

export type {
  CmmdCloudProjectRemovalOperation,
  CmmdCloudProjectRemovalPreview,
} from "./cmmdCloudProjectRemovalApi";
export type {
  CmmdForgeRuntimeApiConfig,
  CmmdForgeRuntimeLifecycleAction,
} from "./cmmdRuntimeRequest";

// Everything catalog-shaped lives in `cloudProjectCatalog.ts` and is imported
// from there by its consumers. Re-export hops hide which module owns a type,
// and they make dead-code analysis less reliable.

export interface CmmdForgeRuntimeRecord {
  readonly runtimeId: string;
  readonly organizationId: number;
  readonly userId: number;
  readonly projectId: number;
  readonly desiredState: string;
  readonly status: string;
  readonly readinessPhase: string;
  /**
   * First-class Sprite API name from CMMD #5456. Prefer this over
   * `flyMachineId` via `resolveCmmdRuntimeSpriteName`.
   */
  readonly spriteName?: string | null;
  /** Sprites resource UUID from CMMD #5456. Null on historical Fly rows. */
  readonly spriteId?: string | null;
  readonly flyAppName: string | null;
  /**
   * Temporary bridge column until CMMD #5458. Do not treat as operational
   * identity when `spriteName` is present (including explicit null).
   */
  readonly flyMachineId: string | null;
  readonly routeKey: string;
  readonly workspaceRoot: string;
  readonly githubRepoFullName: string | null;
}

export interface CmmdForgeRuntimeConnectionDescriptor {
  readonly runtime: CmmdForgeRuntimeRecord;
  readonly httpBaseUrl: string;
  readonly wsBaseUrl: string;
  readonly bearerToken: string;
  readonly expiresAt: string;
}

/**
 * What POSTing an in-place commit update now returns.
 *
 * CMMD made this route durable: it answers 202 with the accepted operation
 * rather than 200 with a finished update. The commit details (`targetCommit`,
 * `previousCommit`, `alreadyCurrent`) moved onto the operation's `result` and
 * are only readable once the operation reaches a terminal status by polling
 * `GET /update-commit/{operationId}`.
 */
export type CmmdForgeRuntimeCommitUpdateOperation =
  CmmdForgePaths["/api/forge/context/runtimes/{runtimeId}/update-commit"]["post"]["responses"][202]["content"]["application/json"]["data"]["operation"];
export type CmmdForgeRuntimeCommitUpdateOperationStatus =
  CmmdForgePaths["/api/forge/context/runtimes/{runtimeId}/update-commit/{operationId}"]["get"]["responses"][200]["content"]["application/json"]["data"]["operation"];

export interface EnsureCmmdForgeRuntimeInput {
  readonly projectId?: number;
  readonly organizationId?: number;
  readonly githubRepoFullName?: string | null;
  readonly workspaceRoot?: string | null;
  readonly signal?: AbortSignal;
}

export interface DestroyCmmdForgeRuntimeInput {
  readonly runtimeId: string;
  readonly organizationId?: number;
  readonly purgeVolume?: boolean;
  /**
   * Bypass CMMD's drain guard. Only for a runtime whose destroy already failed
   * 409: a wedged workspace's in-flight turn never completes, so its
   * `activeTurnCount` never decrements and the guard refuses forever. Discards
   * work still running there — never send this speculatively.
   */
  readonly force?: boolean;
  readonly reason?: string;
  readonly signal?: AbortSignal;
}

export interface CmmdForgeRuntimeLifecycleInput {
  readonly runtimeId: string;
  readonly organizationId?: number;
  readonly action: CmmdForgeRuntimeLifecycleAction;
  readonly signal?: AbortSignal;
}

function runtimePath(runtimeId: string, suffix = ""): string {
  return `/api/forge/context/runtimes/${encodeURIComponent(runtimeId)}${suffix}`;
}

/**
 * Translate a non-ok cloud-catalog proxy response into a typed `CmmdApiError`.
 * 401/403 are raised as `kind:"auth"` so the catalog resource can distinguish a
 * signed-out session (retry after re-auth) from a transient server failure
 * (keep the cached rows).
 */
async function throwIfCloudProjectProxyFailed(res: Response): Promise<void> {
  if (res.ok) return;
  const { CmmdApiError } = await import("../cmmdApi/transport");
  if (res.status === 401 || res.status === 403) {
    let code: string | null = null;
    try {
      const body = (await res.json()) as Record<string, unknown> | null;
      code = typeof body?.["code"] === "string" ? body["code"] : null;
    } catch {
      // ignore parse error
    }
    throw new CmmdApiError({
      kind: "auth",
      message: "CMMD authentication required. Sign in again to continue.",
      status: res.status,
      code,
    });
  }
  throw new CmmdApiError({
    kind: res.status >= 500 ? "server" : "client",
    message: `forge-cloud-projects proxy error (${res.status})`,
    status: res.status,
  });
}

export class CmmdForgeRuntimeApiClient {
  constructor(private readonly config: CmmdForgeRuntimeApiConfig) {}

  async ensureRuntime(input: EnsureCmmdForgeRuntimeInput): Promise<CmmdForgeRuntimeRecord> {
    const response = await requestRuntime<{ runtime: CmmdForgeRuntimeRecord }>(
      this.config,
      {
        method: "POST",
        path: "/api/forge/context/runtimes/ensure",
        body: {
          ...(input.projectId !== undefined ? { projectId: input.projectId } : {}),
          ...(input.organizationId !== undefined ? { organizationId: input.organizationId } : {}),
          ...(input.githubRepoFullName !== undefined
            ? { githubRepoFullName: input.githubRepoFullName }
            : {}),
          ...(input.workspaceRoot !== undefined ? { workspaceRoot: input.workspaceRoot } : {}),
        },
        ...(input.signal !== undefined ? { signal: input.signal } : {}),
      },
      this.config.ensureUrl ? () => this.config.ensureUrl! : undefined,
    );
    return response.runtime;
  }

  async connectRuntime(input: {
    readonly runtimeId: string;
    readonly organizationId?: number;
    readonly signal?: AbortSignal;
  }): Promise<CmmdForgeRuntimeConnectionDescriptor> {
    return requestRuntime<CmmdForgeRuntimeConnectionDescriptor>(this.config, {
      method: "POST",
      path: runtimePath(input.runtimeId, "/connect"),
      ...(input.organizationId !== undefined
        ? { body: { organizationId: input.organizationId } }
        : {}),
      ...(input.signal !== undefined ? { signal: input.signal } : {}),
    });
  }

  async getRuntime(input: {
    readonly runtimeId: string;
    readonly signal?: AbortSignal;
  }): Promise<CmmdForgeRuntimeRecord> {
    const response = await requestRuntime<{ runtime: CmmdForgeRuntimeRecord }>(this.config, {
      method: "GET",
      path: runtimePath(input.runtimeId),
      ...(input.signal !== undefined ? { signal: input.signal } : {}),
    });
    return response.runtime;
  }

  async destroyRuntime(input: DestroyCmmdForgeRuntimeInput): Promise<CmmdForgeRuntimeRecord> {
    const response = await requestRuntime<{ runtime: CmmdForgeRuntimeRecord }>(
      this.config,
      {
        method: "POST",
        path: runtimePath(input.runtimeId, "/destroy"),
        body: {
          purgeVolume: input.purgeVolume ?? false,
          // Omitted unless explicitly requested — CMMD's schema is `.strict()`,
          // and an absent field is the safe default on a destructive call.
          ...(input.force === true ? { force: true } : {}),
          ...(input.reason !== undefined ? { reason: input.reason } : {}),
          ...(input.organizationId !== undefined ? { organizationId: input.organizationId } : {}),
        },
        ...(input.signal !== undefined ? { signal: input.signal } : {}),
      },
      this.config.destroyUrl ? () => this.config.destroyUrl!(input.runtimeId) : undefined,
    );
    return response.runtime;
  }

  async runRuntimeLifecycleAction(
    input: CmmdForgeRuntimeLifecycleInput,
  ): Promise<CmmdForgeRuntimeRecord> {
    const response = await requestRuntime<{ runtime: CmmdForgeRuntimeRecord }>(
      this.config,
      {
        method: "POST",
        path: runtimePath(input.runtimeId, `/${input.action}`),
        body: input.organizationId !== undefined ? { organizationId: input.organizationId } : {},
        ...(input.signal !== undefined ? { signal: input.signal } : {}),
      },
      this.config.lifecycleUrl
        ? () => this.config.lifecycleUrl!(input.runtimeId, input.action)
        : undefined,
    );
    return response.runtime;
  }

  async updateRuntimeCommit(input: {
    readonly runtimeId: string;
    readonly organizationId?: number;
    readonly signal?: AbortSignal;
  }): Promise<CmmdForgeRuntimeCommitUpdateOperation> {
    const response = await requestRuntime<{ operation: CmmdForgeRuntimeCommitUpdateOperation }>(
      this.config,
      {
        method: "POST",
        path: runtimePath(input.runtimeId, "/update-commit"),
        body: input.organizationId !== undefined ? { organizationId: input.organizationId } : {},
        ...(input.signal !== undefined ? { signal: input.signal } : {}),
      },
      this.config.updateCommitUrl ? () => this.config.updateCommitUrl!(input.runtimeId) : undefined,
    );
    return response.operation;
  }

  async getRuntimeCommitUpdateOperation(input: {
    readonly runtimeId: string;
    readonly operationId: number;
    readonly signal?: AbortSignal;
  }): Promise<CmmdForgeRuntimeCommitUpdateOperationStatus> {
    const response = await requestRuntime<{
      operation: CmmdForgeRuntimeCommitUpdateOperationStatus;
    }>(
      this.config,
      {
        method: "GET",
        path: runtimePath(
          input.runtimeId,
          `/update-commit/${encodeURIComponent(String(input.operationId))}`,
        ),
        ...(input.signal !== undefined ? { signal: input.signal } : {}),
      },
      this.config.updateCommitOperationUrl
        ? () => this.config.updateCommitOperationUrl!(input.runtimeId, input.operationId)
        : undefined,
    );
    return response.operation;
  }

  /**
   * List every PROJECT on the authenticated user's Forge Cloud surface.
   *
   * Project-keyed, not runtime-keyed: a project the user registered but has
   * never provisioned comes back with `runtime: null` rather than being absent.
   * The previous runtime-keyed list could not represent that state at all,
   * which is why import had to eagerly provision a VM.
   *
   * When `config.listUrl` is set the request goes directly to that URL
   * (same-origin proxy path) with `credentials: "include"` and no
   * Authorization header — the server attaches the CMMD token.
   *
   * Also carries the org's Forge Cloud warm-pool `capacity` (`null` when CMMD
   * has not resolved it) — see `ForgeCloudCapacity`.
   *
   * BOTH legs return a `CloudProjectCatalog` built by the one validator. That
   * is not tidiness: the type has no public array and no object literal
   * inhabits it, so neither leg can be made to yield a silently-empty catalog,
   * and callers ask about a project through `locate()` instead of reading rows
   * and inferring. See `cloudProjectCatalog.ts`.
   */
  async listCloudProjects(input?: { readonly signal?: AbortSignal }): Promise<CloudProjectCatalog> {
    if (this.config.listUrl) {
      const bearer = ensureBearer(this.config);
      const res = await fetch(this.config.listUrl, {
        method: "GET",
        headers: {
          Accept: "application/json",
          ...(bearer ? { Authorization: `Bearer ${bearer}` } : {}),
        },
        credentials: bearer ? "omit" : "include",
        // Removal proves a project is gone by RE-READING this catalog, so the
        // proof is only worth anything against a fresh read. The Forge proxy
        // sets no cache headers on this route today, which makes "uncached"
        // an unpinned server-side assumption — and a cached EMPTY read would
        // fail silently, in the one direction that reports a false success.
        // Pin it on the client instead of depending on that default holding.
        cache: "no-store",
        ...(input?.signal !== undefined ? { signal: input.signal } : {}),
      });
      await throwIfCloudProjectProxyFailed(res);
      return readCloudProjectCatalog(await res.json());
    }
    // Routed through the SAME reader as the proxy leg on purpose: handing
    // `response.projects` straight through let an absent key become
    // `rows: undefined`, which every caller's `?? []` turned back into "the
    // catalog is empty" — the same fail-open through a different door.
    const response = await requestRuntime<unknown>(this.config, {
      method: "GET",
      path: "/api/forge/context/cloud-projects",
      ...(input?.signal !== undefined ? { signal: input.signal } : {}),
    });
    return readCloudProjectCatalog(response);
  }

  // Called by sidebarProjectRemoval through the control-plane client.
  async previewCloudProjectRemoval(input: {
    readonly projectId: number;
    readonly signal?: AbortSignal;
  }): Promise<CmmdCloudProjectRemovalPreview> {
    return previewCloudProjectRemoval(this.config, input);
  }

  // Called by sidebarProjectRemoval through the control-plane client.
  async requestCloudProjectRemoval(input: {
    readonly projectId: number;
    readonly idempotencyKey: string;
    readonly deletePlanningProject: boolean;
    readonly deleteCheckoutFiles?: boolean;
    readonly previewToken?: string;
    readonly forceDirty?: boolean;
    readonly signal?: AbortSignal;
  }): Promise<CmmdCloudProjectRemovalOperation> {
    return requestCloudProjectRemoval(this.config, input);
  }

  async getCloudProjectRemoval(input: {
    readonly operationId: string;
    readonly signal?: AbortSignal;
  }): Promise<CmmdCloudProjectRemovalOperation> {
    const response = await requestRuntime<{ operation: CmmdCloudProjectRemovalOperation }>(
      this.config,
      {
        method: "GET",
        path: `/api/forge/context/cloud-projects/removals/${encodeURIComponent(input.operationId)}`,
        ...(input.signal !== undefined ? { signal: input.signal } : {}),
      },
      this.config.cloudProjectRemovalReceiptUrl
        ? () => this.config.cloudProjectRemovalReceiptUrl!(input.operationId)
        : undefined,
    );
    return response.operation;
  }
}
